import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  History, 
  Target, 
  TrendingUp, 
  TrendingDown,
  CheckCircle2, 
  XCircle, 
  Clock,
  Trophy,
  ChevronLeft,
  Loader2,
  Users,
  BarChart3,
  Flame
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PredictionHistory() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [leagueFilter, setLeagueFilter] = useState('all');
  const [leagues, setLeagues] = useState([]);

  useEffect(() => {
    fetchLeagues();
    if (isAuthenticated) {
      fetchPredictions();
    } else {
      setLoading(false);
    }
    fetchLeaderboard();
  }, [isAuthenticated, statusFilter, leagueFilter]);

  const fetchLeagues = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leagues`);
      if (response.ok) {
        const data = await response.json();
        setLeagues(data);
      }
    } catch (err) {
      console.error('Failed to fetch leagues:', err);
    }
  };

  const fetchPredictions = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/predictions/user/history?limit=50`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (leagueFilter !== 'all') {
        url += `&league_id=${leagueFilter}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPredictions(data.predictions || []);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch predictions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/predictions/leaderboard`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 70) return 'text-green-400';
    if (accuracy >= 55) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold uppercase mb-2">Sign In Required</h2>
            <p className="text-muted-foreground mb-4">Sign in to view your prediction history</p>
            <Button onClick={() => navigate('/login')} className="rounded-sm">
              Sign In
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-2 mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-sm"
                onClick={() => navigate('/board')}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Board
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500/10 rounded-sm flex items-center justify-center">
                <History className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold uppercase tracking-tight">
                  Prediction History
                </h1>
                <p className="text-muted-foreground">
                  Your predictions, results, and accuracy stats
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Tabs defaultValue="history" className="w-full">
            <TabsList className="w-full max-w-md mb-6">
              <TabsTrigger value="history" className="flex-1" data-testid="history-tab">
                My Predictions
              </TabsTrigger>
              <TabsTrigger value="stats" className="flex-1" data-testid="stats-tab">
                My Stats
              </TabsTrigger>
              <TabsTrigger value="leaderboard" className="flex-1" data-testid="leaderboard-tab">
                Leaderboard
              </TabsTrigger>
            </TabsList>

            {/* Prediction History Tab */}
            <TabsContent value="history">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 mb-6">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px] rounded-sm">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={leagueFilter} onValueChange={setLeagueFilter}>
                  <SelectTrigger className="w-[150px] rounded-sm">
                    <SelectValue placeholder="League" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Leagues</SelectItem>
                    {leagues.map(league => (
                      <SelectItem key={league.league_id} value={league.league_id}>
                        {league.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full" />
                  ))}
                </div>
              ) : predictions.length === 0 ? (
                <div className="text-center py-12 bg-card border border-border rounded-sm">
                  <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-display text-xl font-bold uppercase mb-2">
                    No Predictions Yet
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Make predictions on upcoming games to track your accuracy
                  </p>
                  <Button onClick={() => navigate('/board')} className="rounded-sm">
                    View Games
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {predictions.map((pred) => (
                    <Link 
                      key={pred.prediction_id}
                      to={`/game/${pred.game_id}`}
                      className="block bg-card border border-border rounded-sm p-4 hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Status Badge */}
                          {pred.status === 'pending' ? (
                            <div className="w-10 h-10 bg-yellow-500/10 rounded-sm flex items-center justify-center">
                              <Clock className="w-5 h-5 text-yellow-400" />
                            </div>
                          ) : pred.is_correct ? (
                            <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                              <CheckCircle2 className="w-5 h-5 text-green-400" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 bg-red-500/10 rounded-sm flex items-center justify-center">
                              <XCircle className="w-5 h-5 text-red-400" />
                            </div>
                          )}
                          
                          <div>
                            <p className="font-medium">
                              {pred.away_team_name} @ {pred.home_team_name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Picked: <span className="font-medium">
                                {pred.predicted_winner === 'home' ? pred.home_team_name : pred.away_team_name}
                              </span>
                              {pred.agreed_with_platform && (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  Agreed with platform
                                </Badge>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <Badge variant="outline" className="mb-1">
                            {pred.league_id?.toUpperCase()}
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            {new Date(pred.game_start_time).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Stats Tab */}
            <TabsContent value="stats">
              {stats ? (
                <div className="space-y-6">
                  {/* Overall Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-card border border-border rounded-sm p-6 text-center">
                      <p className="font-mono text-4xl font-bold">{stats.total_predictions}</p>
                      <p className="text-sm text-muted-foreground">Total Predictions</p>
                    </div>
                    <div className="bg-card border border-border rounded-sm p-6 text-center">
                      <p className="font-mono text-4xl font-bold text-green-400">{stats.correct_predictions}</p>
                      <p className="text-sm text-muted-foreground">Correct</p>
                    </div>
                    <div className="bg-card border border-border rounded-sm p-6 text-center">
                      <p className={`font-mono text-4xl font-bold ${getAccuracyColor(stats.accuracy_percentage)}`}>
                        {stats.accuracy_percentage}%
                      </p>
                      <p className="text-sm text-muted-foreground">Accuracy</p>
                    </div>
                    <div className="bg-card border border-border rounded-sm p-6 text-center">
                      <p className="font-mono text-4xl font-bold text-yellow-400">{stats.pending_predictions}</p>
                      <p className="text-sm text-muted-foreground">Pending</p>
                    </div>
                  </div>

                  {/* Platform Comparison */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-card border border-border rounded-sm p-6">
                      <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                        When You Agreed with Platform
                      </h3>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Progress 
                            value={stats.agreed_with_platform?.accuracy || 0} 
                            className="h-3"
                          />
                        </div>
                        <span className={`font-mono font-bold ${getAccuracyColor(stats.agreed_with_platform?.accuracy || 0)}`}>
                          {stats.agreed_with_platform?.accuracy || 0}%
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {stats.agreed_with_platform?.correct || 0} / {stats.agreed_with_platform?.total || 0} correct
                      </p>
                    </div>
                    
                    <div className="bg-card border border-border rounded-sm p-6">
                      <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                        When You Disagreed with Platform
                      </h3>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Progress 
                            value={stats.disagreed_with_platform?.accuracy || 0} 
                            className="h-3"
                          />
                        </div>
                        <span className={`font-mono font-bold ${getAccuracyColor(stats.disagreed_with_platform?.accuracy || 0)}`}>
                          {stats.disagreed_with_platform?.accuracy || 0}%
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {stats.disagreed_with_platform?.correct || 0} / {stats.disagreed_with_platform?.total || 0} correct
                      </p>
                    </div>
                  </div>

                  {/* By League */}
                  {stats.by_league && Object.keys(stats.by_league).length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-6">
                      <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                        Accuracy by League
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(stats.by_league).map(([league, data]) => (
                          <div key={league} className="text-center p-4 bg-secondary/30 rounded-sm">
                            <p className="font-bold uppercase mb-1">{league}</p>
                            <p className={`font-mono text-2xl font-bold ${getAccuracyColor(data.accuracy)}`}>
                              {data.accuracy}%
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {data.correct}/{data.total}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No stats available yet</p>
                </div>
              )}
            </TabsContent>

            {/* Leaderboard Tab */}
            <TabsContent value="leaderboard">
              <div className="bg-card border border-border rounded-sm">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                      Top Predictors
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Minimum 10 predictions required to qualify
                  </p>
                </div>
                
                {leaderboard.length === 0 ? (
                  <div className="p-8 text-center">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      No leaderboard data yet. Be the first to make 10+ predictions!
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {leaderboard.map((leader, idx) => (
                      <div 
                        key={leader.user_id}
                        className={`flex items-center justify-between p-4 ${
                          leader.user_id === user?.user_id ? 'bg-primary/5' : ''
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span className={`font-mono text-2xl font-bold w-8 ${
                            idx === 0 ? 'text-yellow-400' :
                            idx === 1 ? 'text-zinc-400' :
                            idx === 2 ? 'text-orange-400' :
                            'text-muted-foreground'
                          }`}>
                            {leader.rank}
                          </span>
                          <div>
                            <p className="font-medium">
                              {leader.name}
                              {leader.user_id === user?.user_id && (
                                <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {leader.correct_predictions}/{leader.total_predictions} predictions
                            </p>
                          </div>
                        </div>
                        <span className={`font-mono text-xl font-bold ${getAccuracyColor(leader.accuracy)}`}>
                          {leader.accuracy}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
}
